import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext,
} from "./ui/carousel";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { ArrowRight } from "lucide-react";

const FALLBACK = [
  {
    id: "f1",
    title: "Competitive Rates from Our Partners",
    subtitle: "Interest rates may be available subject to lender eligibility and approval.",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    link: "/loans/personal-loan",
    cta_label: "Check Eligibility",
  },
  {
    id: "f2",
    title: "Guided, Transparent Application Support",
    subtitle: "We help you apply — the lender independently decides on approval.",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    link: "/loans/home-loan",
    cta_label: "Explore Home Loans",
  },
  {
    id: "f3",
    title: "Protect Your Family with Insurance",
    subtitle: "Insurance options via authorised partners, subject to policy terms.",
    image: "https://images.unsplash.com/photo-1559734840-f9509ee5677f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    link: "/insurance",
    cta_label: "View Insurance",
  },
];

function Slide({ o, embedded }) {
  const isInternal = (o.link || "").startsWith("/");
  const CTA = (
    <Button size={embedded ? "default" : "lg"} className={embedded ? "h-10 px-5 font-semibold" : "h-11 px-6 text-base font-semibold"}>
      {o.cta_label || "Apply Now"} <ArrowRight className="ml-1.5 h-4 w-4" />
    </Button>
  );
  return (
    <div className={`relative overflow-hidden rounded-2xl ${embedded ? "h-[260px] md:h-[300px]" : "h-[300px] md:h-[380px]"}`}>
      {o.image ? (
        <img src={o.image} alt={o.title} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary to-[#1b6fa8]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
      <div className="relative flex h-full max-w-xl flex-col justify-center px-6 md:px-10">
        <h3 className={`font-heading font-bold text-white ${embedded ? "text-xl md:text-2xl" : "text-2xl md:text-4xl"}`}>{o.title}</h3>
        {o.subtitle && <p className={`mt-2 text-white/85 ${embedded ? "text-sm md:text-base" : "md:text-lg"}`}>{o.subtitle}</p>}
        {(o.link || o.cta_label) && (
          <div className="mt-4">
            {isInternal ? <Link to={o.link || "/contact"}>{CTA}</Link>
              : <a href={o.link || "#"} target="_blank" rel="noopener noreferrer">{CTA}</a>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function OfferSlider({ embedded = false }) {
  const [offers, setOffers] = useState(null);
  const [emblaApi, setEmblaApi] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/offers")
      .then(({ data }) => { if (active) setOffers(data && data.length ? data : FALLBACK); })
      .catch(() => { if (active) setOffers(FALLBACK); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const id = setInterval(() => {
      if (emblaApi.canScrollNext()) emblaApi.scrollNext();
      else emblaApi.scrollTo(0);
    }, 5000);
    return () => clearInterval(id);
  }, [emblaApi]);

  const slides = useMemo(() => offers || [], [offers]);

  const skeletonH = embedded ? "h-[260px] md:h-[300px]" : "h-[300px] md:h-[380px]";
  if (offers === null) {
    const box = <div className={`w-full animate-pulse rounded-2xl bg-secondary ${skeletonH}`} />;
    return embedded ? box : <div className="container-page py-10">{box}</div>;
  }
  if (!slides.length) return null;

  const carousel = (
    <Carousel setApi={setEmblaApi} opts={{ loop: true }} className="relative">
      <CarouselContent>
        {slides.map((o) => (
          <CarouselItem key={o.id}><Slide o={o} embedded={embedded} /></CarouselItem>
        ))}
      </CarouselContent>
      {slides.length > 1 && (
        <>
          <CarouselPrevious className="left-3 h-9 w-9 border-0 bg-white/90 text-primary hover:bg-white" />
          <CarouselNext className="right-3 h-9 w-9 border-0 bg-white/90 text-primary hover:bg-white" />
        </>
      )}
    </Carousel>
  );

  if (embedded) return carousel;

  return (
    <section className="py-10 md:py-12">
      <div className="container-page">{carousel}</div>
    </section>
  );
}
